import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import "core-js/stable/atob";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsSystemAdmin,
  setUserData,
  setUserReadonly,
} from "../store/slice/usageInfoSlice";
import { store } from "../store/store";
import { getGroupID, resetDataStore } from "./GroupContext";
import authService from "./auth/auth.service";

// auth context stores the token and the user info which can be accessed from anywhere inside the app
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const { showBoundary } = useErrorBoundary();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.usageInfo.user);
  const [user, setUser] = useState(
    userData && userData !== "undefined" ? userData : null,
  );
  const userReadonly = useSelector((state) => state.usageInfo.userReadonly);
  const [readonly, setReadonly] = useState(userReadonly ? userReadonly : false);
  const userGroupRole = useSelector((state) => state.usageInfo.userGroupRole);
  const isTenantAdmin = useSelector(state => state.usageInfo.isTenantAdmin);
  const isSystemAdmin = useSelector(state => state.usageInfo.isSystemAdmin);

  useEffect(() => {
    if (user) {
      dispatch(setUserData(user));
    }
  }, [user]);

  useEffect(() => {
    if (readonly !== null) {
      dispatch(setUserReadonly(readonly));
    }
  }, [readonly]);

  // call this function when you want to authenticate the user
  const login = async (data) => {
    setUser(data);

    const isSystemAdmin = data?.isSystemAdmin || false;
    dispatch(setIsSystemAdmin(isSystemAdmin));

    dispatch(setUserData(data));
  };

  // call this function to sign out logged in user
  const logout = async () => {
    await authService.logout();

    setUser(null);
    resetDataStore();
  };

  const getInitials = () => {
    if (user) {
      try {
        const userDetails = jwtDecode(user?.id_token);
        const initials = userDetails
          ? userDetails?.given_name[0] + userDetails?.family_name[0]
          : "";
        return initials;
      } catch {
        return "";
      }
    }
    return "";
  };

  const getFirstName = () => {
    if (user) {
      try {
        const userDetails = jwtDecode(user?.id_token);
        const name = userDetails ? userDetails?.given_name : "";
        return name;
      } catch {
        return "";
      }
    }
    return "";
  };

  const getEmail = () => {
    if (user) {
      try {
        const userDetails = jwtDecode(user?.id_token);
        // Entra ID uses 'preferred_username' for email
        return userDetails?.preferred_username || userDetails?.upn || userDetails?.email;
      } catch {
        return "";
      }
    }
    return "";
  };

  const hasUser = () => {
    return !!userData;
  };

  const isAdmin = () => {
    if (hasUser()) {
      return Boolean(isSystemAdmin || isTenantAdmin);
    }
    showBoundary("User is not logged in");
  };

  const isReadOnlyUser = () => {
    const groupId = getGroupID();
    if (hasUser()) {
      if (userReadonly) {
        return true;
      }
      if (isAdmin()) {
        return false;
      }
      return userGroupRole === "Reader";
    }
    // throw error here if user is not logged in?
  };

  const userCanManageAccess = () => {
    if (hasUser()) {
      if (isReadOnlyUser() || userReadonly) {
        return false;
      }
      if (isAdmin()) {
        return true;
      }
      const isTenantAdmin = store.getState().usageInfo.isTenantAdmin;
      const isGroupAdminOrSupervisor = ["Admin", "Supervisor"].includes(
        store.getState().usageInfo.userGroupRole,
      );
      const canManage = isTenantAdmin || isGroupAdminOrSupervisor;
      return canManage;
    }
    return false;
  };

  const value = useMemo(
    () => ({
      getInitials,
      hasUser,
      login,
      logout,
      isReadOnlyUser,
      userCanManageAccess,
      getEmail,
      getFirstName,
      setReadonly,
      isAdmin,
    }),
    [user],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const auth = useContext(AuthContext);
  return auth;
  //return useContext(AuthContext)
};

export { AuthContext, AuthProvider };
